import { ReactNode, useState, useEffect } from 'react'
import {
  AppBar as MaterialAppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  useScrollTrigger,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Divider
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link, useNavigate } from 'react-router-dom'
import { AccountCircle, ExitToApp } from '@mui/icons-material'
import { getAuth, signOut, User } from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import ProIndicator from './ProIndicator'
import AboutModal from './AboutModal'
import ContactModal from './ContactModal'
import SupportModal from './SupportModal'

type Props = {
  title?: string
  linkBackTo?: string
  actions?: ReactNode
  user?: User | null
  showUserMenu?: boolean
  onLoginClick?: () => void
  onRegisterClick?: () => void
}

const AppBar = (props: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [userSubscription, setUserSubscription] = useState<any>(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [aboutModalOpen, setAboutModalOpen] = useState(false)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [supportModalOpen, setSupportModalOpen] = useState(false)
  const navigate = useNavigate()
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 40
  })

  useEffect(() => {
    const fetchUserSubscription = async () => {
      if (!props.user) {
        setUserSubscription(null)
        setSubscriptionLoading(false)
        return
      }

      try {
        const db = getFirestore()
        const userDoc = await getDoc(doc(db, 'webUsers', props.user.uid))

        if (userDoc.exists()) {
          const userData = userDoc.data()
          setUserSubscription(userData.subscription || null)
        } else {
          setUserSubscription(null)
        }
      } catch (error) {
        console.error('Error fetching user subscription:', error)
        setUserSubscription(null)
      } finally {
        setSubscriptionLoading(false)
      }
    }

    fetchUserSubscription()
  }, [props.user])

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      const auth = getAuth()
      await signOut(auth)
      navigate('/')
      handleUserMenuClose()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const handleMenuItemClick = (path: string) => {
    navigate(path)
    handleUserMenuClose()
  }

  return (
    <MaterialAppBar
      elevation={0}
      position='sticky'
      sx={{
        background: trigger 
          ? 'rgba(30, 41, 59, 0.95)' 
          : 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: trigger 
          ? '1px solid rgba(74, 144, 194, 0.2)' 
          : '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: trigger 
          ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
          : '0 2px 10px rgba(0, 0, 0, 0.05)'
      }}
    >
      <Toolbar>
        {props.linkBackTo && (
          <IconButton
            component={Link}
            to={props.linkBackTo}
            color='inherit'
            edge='start'
          >
            <ArrowBackIcon titleAccess='Navigate Back' />
          </IconButton>
        )}
        {!props.linkBackTo && (
          <Box 
            component={Link}
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            {/* Modern R Logo */}
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 2.5,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(74, 144, 194, 0.3)',
              position: 'relative',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 20px rgba(74, 144, 194, 0.2)',
                border: '1px solid rgba(74, 144, 194, 0.5)'
              }
            }}>
              <Typography sx={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: 800,
                fontFamily: '"Inter", system-ui, sans-serif',
                letterSpacing: '-0.01em'
              }}>
                R
              </Typography>
            </Box>
            <Box sx={{ cursor: 'pointer' }}>
              <Typography variant='h5' sx={{
                fontWeight: 800,
                color: '#ffffff',
                fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-0.01em',
                fontSize: '1.6rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.9)'
                }
              }}>
                Rafilis
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: '"Inter", sans-serif',
                letterSpacing: '0.5px',
                mt: -0.5,
                transition: 'color 0.2s ease'
              }}>
                AUDIO SOLUTIONS
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />

        {/* Center Navigation */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center', 
          gap: 1,
          mx: 'auto'
        }}>
          <Button
            onClick={() => window.open('https://app.rafilis.com', '_blank')}
            sx={{
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              background: '#2048BC',
              '&:hover': {
                background: '#1a3d9e',
                boxShadow: '0 2px 8px rgba(32, 72, 188, 0.3)'
              }
            }}
          >
            Control Panel
          </Button>
          <Button
            onClick={() => setAboutModalOpen(true)}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '0.9rem',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            About
          </Button>
          <Button
            onClick={() => setSupportModalOpen(true)}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '0.9rem',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Support
          </Button>
          <Button
            onClick={() => setContactModalOpen(true)}
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '0.9rem',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Contact
          </Button>
        </Box>

        {/* Right Side - Auth Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
          {props.user && (
            <ProIndicator
              subscription={userSubscription}
              loading={subscriptionLoading}
            />
          )}

          {props.user ? (
            <>
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  '&:hover': {
                    background: 'rgba(74, 144, 194, 0.15)',
                    border: '1px solid rgba(74, 144, 194, 0.3)'
                  }
                }}
              >
                <Avatar sx={{ 
                  width: 32, 
                  height: 32, 
                  fontSize: '0.9rem',
                  background: 'linear-gradient(135deg, #4A90C2, #2E86AB)'
                }}>
                  {(props.user.displayName?.charAt(0) || props.user.email?.charAt(0) || 'U').toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: {
                    background: 'rgba(30, 41, 59, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    minWidth: 180
                  }
                }}
              >
                <MenuItem onClick={() => handleMenuItemClick('/dashboard')} sx={{ color: '#ffffff' }}>
                  <AccountCircle sx={{ mr: 1 }} />
                  Dashboard
                </MenuItem>
                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                <MenuItem onClick={handleLogout} sx={{ color: '#ffffff' }}>
                  <ExitToApp sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                onClick={props.onLoginClick}
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  minWidth: 'auto',
                  px: 2.5,
                  py: 1,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#ffffff',
                    background: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Login
              </Button>
              <Button
                onClick={props.onRegisterClick}
                sx={{
                  color: '#ffffff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  background: '#2048BC',
                  borderRadius: '8px',
                  px: 2.5,
                  py: 1,
                  minWidth: 'auto',
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#1a3d9e',
                    boxShadow: '0 2px 8px rgba(32, 72, 188, 0.3)'
                  }
                }}
              >
                Register
              </Button>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Modals */}
      <AboutModal
        open={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
      <SupportModal
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
      />
    </MaterialAppBar>
  )
}

export default AppBar
